/* Claro CMS — section catalog + mini wireframe previews.
   Presentation-layer metadata that powers the "Add a section" picker: every
   addable module type is sorted into a plain-language intent group, given a
   "Best for…" sentence, an icon, and a mini wireframe sketch so advisors can
   recognise a section by its shape rather than its name.

   This is intentionally a client-only concern. The functional rules
   (which types are addable, repeatable, required) live in cms-editor-state.ts
   and the backend descriptors; this file only adds the copy + visuals. */
import React from 'react';
import {
    LuColumns3,
    LuFileText,
    LuImage,
    LuListOrdered,
    LuMail,
    LuMegaphone,
    LuMessageSquareQuote,
    LuStar,
    LuUser,
} from 'react-icons/lu';

import { CmsPageModuleType } from '@ndvr/cms-api';

/* ---------- intent groups ---------- */

export type CmsSectionGroupKey = 'first' | 'trust' | 'offer' | 'convert';
export type CmsSectionGroupTone = 'aqua' | 'gold' | 'cocoa';

export interface CmsSectionGroup {
    key: CmsSectionGroupKey;
    label: string;
    sub: string;
    tone: CmsSectionGroupTone;
}

/* Order here is the order groups stack in the picker. */
export const CMS_SECTION_GROUPS: CmsSectionGroup[] = [
    { key: 'first', label: 'First impression', sub: 'The first thing a visitor sees', tone: 'aqua' },
    { key: 'trust', label: 'Build trust', sub: 'Establish who you are and why you’re credible', tone: 'gold' },
    { key: 'offer', label: 'Explain offerings', sub: 'Make what you do easy to understand', tone: 'cocoa' },
    { key: 'convert', label: 'Convert visitors', sub: 'Turn interest into a conversation', tone: 'aqua' },
];

export const CMS_SECTION_GROUP_OF: Record<CmsSectionGroupKey, CmsSectionGroup> = CMS_SECTION_GROUPS.reduce(
    (acc, group) => {
        acc[group.key] = group;
        return acc;
    },
    {} as Record<CmsSectionGroupKey, CmsSectionGroup>,
);

/* ---------- per-type catalog ---------- */

export type CmsWireKind =
    | 'hero'
    | 'about'
    | 'mediaText'
    | 'cards'
    | 'steps'
    | 'quote'
    | 'resource'
    | 'disclosures'
    | 'contact'
    | 'leadForm'
    | 'faq'
    | 'cta';

export interface CmsSectionCatalogEntry {
    type: CmsPageModuleType;
    name: string;
    group: CmsSectionGroupKey;
    icon: React.ComponentType<{ className?: string }>;
    wire: CmsWireKind;
    bestFor: string;
}

/* Catalog entries for every type a user can add (see CMS_MODULE_PICKER_ORDER in
   cms-editor-state.ts). */
export const CMS_SECTION_CATALOG: CmsSectionCatalogEntry[] = [
    {
        type: CmsPageModuleType.HERO,
        name: 'Hero',
        group: 'first',
        icon: LuStar,
        wire: 'hero',
        bestFor: 'Greeting visitors with your single most important message.',
    },
    {
        type: CmsPageModuleType.ADVISOR_PROFILE,
        name: 'Advisor profile',
        group: 'trust',
        icon: LuUser,
        wire: 'about',
        bestFor: 'Introducing who you are and the standard you hold.',
    },
    {
        type: CmsPageModuleType.MEDIA_TEXT_SECTION,
        name: 'Media + text',
        group: 'trust',
        icon: LuImage,
        wire: 'mediaText',
        bestFor: 'Pairing a photograph with a block of copy to tell part of your story.',
    },
    {
        type: CmsPageModuleType.QUOTE_SECTION,
        name: 'Quote',
        group: 'trust',
        icon: LuMessageSquareQuote,
        wire: 'quote',
        bestFor: 'Letting a happy client speak on your behalf.',
    },
    {
        type: CmsPageModuleType.INFO_CARD_GRID,
        name: 'Info card grid',
        group: 'offer',
        icon: LuColumns3,
        wire: 'cards',
        bestFor: 'Breaking an idea into a few short, scannable cards.',
    },
    {
        type: CmsPageModuleType.PROCESS_STEPS,
        name: 'Process steps',
        group: 'offer',
        icon: LuListOrdered,
        wire: 'steps',
        bestFor: 'Walking visitors through how you work, step by step.',
    },
    {
        type: CmsPageModuleType.RESOURCE_LIST,
        name: 'Resource list',
        group: 'offer',
        icon: LuFileText,
        wire: 'resource',
        bestFor: 'Pointing visitors to approved links, guides, and downloads.',
    },
    {
        type: CmsPageModuleType.IMPORTANT_DISCLOSURES,
        name: 'Important disclosures',
        group: 'trust',
        icon: LuFileText,
        wire: 'disclosures',
        bestFor: 'Making compliance disclosures visible and editable for this microsite.',
    },
    {
        type: CmsPageModuleType.FAQ,
        name: 'FAQ',
        group: 'offer',
        icon: LuListOrdered,
        wire: 'faq',
        bestFor: 'Answering the questions that stall a decision.',
    },
    {
        type: CmsPageModuleType.CONTACT_SECTION,
        name: 'Contact',
        group: 'convert',
        icon: LuMail,
        wire: 'contact',
        bestFor: 'Sharing the ways a visitor can reach you.',
    },
    {
        type: CmsPageModuleType.LEAD_FORM,
        name: 'Lead form',
        group: 'convert',
        icon: LuMail,
        wire: 'leadForm',
        bestFor: 'Collecting visitor details for a follow-up conversation.',
    },
    {
        type: CmsPageModuleType.CTA,
        name: 'Call to action',
        group: 'convert',
        icon: LuMegaphone,
        wire: 'cta',
        bestFor: 'Inviting visitors to take the next step with you.',
    },
];

export const CMS_SECTION_CATALOG_OF: Partial<Record<CmsPageModuleType, CmsSectionCatalogEntry>> =
    CMS_SECTION_CATALOG.reduce(
        (acc, entry) => {
            acc[entry.type] = entry;
            return acc;
        },
        {} as Partial<Record<CmsPageModuleType, CmsSectionCatalogEntry>>,
    );

/* ---------- mini wireframe previews ----------
   Pure CSS sketches (no images, no real copy) that hint at each section's
   shape. Bars are coloured from the Claro cocoa/seafoam ramp; hero + CTA sit
   on a dark cocoa ground with light bars. */

type BarTone = 'strong' | 'mid' | 'soft' | 'faint' | 'aqua';

const BAR_LIGHT: Record<BarTone, string> = {
    strong: 'var(--color-cocoa-500)',
    mid: 'var(--color-cocoa-300)',
    soft: 'var(--color-cocoa-200)',
    faint: 'var(--color-cocoa-100)',
    aqua: 'var(--color-seafoam-300)',
};

const BAR_ON_DARK: Record<BarTone, string> = {
    strong: 'rgba(248,247,245,0.85)',
    mid: 'rgba(248,247,245,0.55)',
    soft: 'rgba(248,247,245,0.32)',
    faint: 'rgba(248,247,245,0.2)',
    aqua: 'var(--color-seafoam-300)',
};

interface WBarProps {
    w?: string;
    h?: number;
    mt?: number;
    c?: BarTone;
    onDark?: boolean;
}

const WBar = ({ w = '100%', h = 6, mt = 0, c = 'mid', onDark = false }: WBarProps) => (
    <span
        style={{
            display: 'block',
            flex: 'none',
            borderRadius: 2,
            width: w,
            height: h,
            marginTop: mt,
            background: (onDark ? BAR_ON_DARK : BAR_LIGHT)[c],
        }}
    />
);

const PAD: React.CSSProperties = {
    height: '100%',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
};
const PAD_CENTER: React.CSSProperties = { ...PAD, alignItems: 'center', justifyContent: 'center', textAlign: 'center' };
const PAD_ROW: React.CSSProperties = { ...PAD, flexDirection: 'row', alignItems: 'stretch', gap: 10 };
const COL: React.CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column' };

const Chip = ({ mt = 12 }: { mt?: number }) => (
    <span style={{ width: 64, height: 13, borderRadius: 999, background: 'var(--color-seafoam-300)', marginTop: mt }} />
);

const wireBody = (kind: CmsWireKind): React.ReactNode => {
    switch (kind) {
        case 'hero':
            return (
                <div style={PAD_CENTER}>
                    <WBar w="34%" h={4} c="aqua" onDark />
                    <WBar w="78%" h={9} mt={7} c="strong" onDark />
                    <WBar w="64%" h={9} mt={3} c="strong" onDark />
                    <WBar w="48%" h={4} mt={8} c="soft" onDark />
                    <Chip />
                </div>
            );
        case 'cta':
            return (
                <div style={PAD_CENTER}>
                    <WBar w="58%" h={9} c="strong" onDark />
                    <WBar w="40%" h={4} mt={8} c="soft" onDark />
                    <Chip />
                </div>
            );
        case 'about':
            return (
                <div style={PAD}>
                    <WBar w="22%" h={4} c="aqua" />
                    <WBar w="82%" h={8} mt={7} c="strong" />
                    <WBar w="100%" h={4} mt={9} c="soft" />
                    <WBar w="100%" h={4} mt={3} c="soft" />
                    <WBar w="60%" h={4} mt={3} c="soft" />
                </div>
            );
        case 'mediaText':
            return (
                <div style={PAD_ROW}>
                    <span
                        style={{
                            flex: 1,
                            borderRadius: 4,
                            background:
                                'repeating-linear-gradient(45deg, var(--color-cocoa-100), var(--color-cocoa-100) 7px, var(--color-cocoa-200) 7px, var(--color-cocoa-200) 14px)',
                        }}
                    />
                    <span style={{ ...COL, justifyContent: 'center' }}>
                        <WBar w="60%" h={4} c="aqua" />
                        <WBar w="92%" h={7} mt={7} c="strong" />
                        <WBar w="100%" h={3} mt={8} c="soft" />
                        <WBar w="100%" h={3} mt={3} c="soft" />
                        <WBar w="70%" h={3} mt={3} c="soft" />
                    </span>
                </div>
            );
        case 'cards':
            return (
                <div style={PAD_ROW}>
                    {[0, 1, 2].map((i) => (
                        <span key={i} style={COL}>
                            <span style={{ height: 2, background: 'var(--color-cocoa-900)', borderRadius: 1 }} />
                            <WBar w="80%" h={6} mt={7} c="strong" />
                            <WBar w="100%" h={3} mt={5} c="soft" />
                            <WBar w="64%" h={3} mt={3} c="soft" />
                        </span>
                    ))}
                </div>
            );
        case 'steps':
            return (
                <div style={PAD}>
                    {[0, 1, 2].map((i) => (
                        <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: i ? 9 : 0 }}>
                            <span
                                style={{
                                    width: 14,
                                    height: 14,
                                    flex: 'none',
                                    borderRadius: 999,
                                    background: 'var(--color-seafoam-300)',
                                }}
                            />
                            <span style={{ flex: 1 }}>
                                <WBar w={`${70 - i * 8}%`} h={5} c="strong" />
                                <WBar w="92%" h={3} mt={4} c="soft" />
                            </span>
                        </span>
                    ))}
                </div>
            );
        case 'quote':
            return (
                <div style={PAD_CENTER}>
                    <WBar w="80%" h={7} c="mid" />
                    <WBar w="66%" h={7} mt={4} c="mid" />
                    <WBar w="30%" h={4} mt={10} c="soft" />
                </div>
            );
        case 'resource':
            return (
                <div style={PAD}>
                    {[0, 1, 2].map((i) => (
                        <span
                            key={i}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '6px 0',
                                borderTop: i ? '1px solid var(--color-cocoa-100)' : 'none',
                            }}
                        >
                            <span style={{ flex: 1 }}>
                                <WBar w={`${72 - i * 6}%`} h={5} c="strong" />
                                <WBar w="90%" h={3} mt={4} c="soft" />
                            </span>
                            <span
                                style={{
                                    width: 14,
                                    height: 8,
                                    borderRadius: 999,
                                    background: 'var(--color-seafoam-300)',
                                    flex: 'none',
                                }}
                            />
                        </span>
                    ))}
                </div>
            );
        case 'disclosures':
            return (
                <div style={PAD}>
                    <WBar w="52%" h={7} c="strong" />
                    <WBar w="100%" h={3} mt={9} c="soft" />
                    <WBar w="96%" h={3} mt={3} c="soft" />
                    <WBar w="88%" h={3} mt={3} c="soft" />
                    <span style={{ borderTop: '1px solid var(--color-cocoa-100)', marginTop: 11, paddingTop: 8 }}>
                        <WBar w="84%" h={3} c="mid" />
                        <WBar w="74%" h={3} mt={4} c="soft" />
                    </span>
                </div>
            );
        case 'contact':
            return (
                <div style={PAD}>
                    <WBar w="40%" h={7} c="strong" />
                    <WBar w="86%" h={3} mt={8} c="soft" />
                    <span style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <WBar w="48%" h={4} c="mid" />
                        <WBar w="48%" h={4} c="mid" />
                    </span>
                    <Chip mt={12} />
                </div>
            );
        case 'leadForm':
            return (
                <div style={PAD_ROW}>
                    <span style={{ ...COL, justifyContent: 'center' }}>
                        <WBar w="34%" h={4} c="aqua" />
                        <WBar w="86%" h={7} mt={7} c="strong" />
                        <WBar w="74%" h={3} mt={8} c="soft" />
                        <WBar w="58%" h={3} mt={3} c="soft" />
                    </span>
                    <span style={{ ...COL, gap: 5, justifyContent: 'center' }}>
                        {[0, 1, 2].map((i) => (
                            <span
                                key={i}
                                style={{
                                    height: 10,
                                    borderRadius: 2,
                                    border: '1px solid var(--color-cocoa-200)',
                                    background: 'var(--color-cocoa-0)',
                                }}
                            />
                        ))}
                        <span style={{ height: 13, borderRadius: 999, background: 'var(--color-seafoam-300)' }} />
                    </span>
                </div>
            );
        case 'faq':
            return (
                <div style={PAD}>
                    {[0, 1, 2].map((i) => (
                        <span
                            key={i}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '7px 0',
                                borderTop: i ? '1px solid var(--color-cocoa-100)' : 'none',
                            }}
                        >
                            <WBar w={i === 1 ? '64%' : '74%'} h={5} c="strong" />
                            <WBar w="92%" h={3} mt={4} c="soft" />
                        </span>
                    ))}
                </div>
            );
        default:
            return null;
    }
};

const DARK_WIRES: ReadonlySet<CmsWireKind> = new Set<CmsWireKind>(['hero', 'cta']);

export const CmsSectionWireframe = ({ kind }: { kind: CmsWireKind }) => (
    <div
        style={{
            aspectRatio: '16 / 9',
            overflow: 'hidden',
            borderBottom: '1px solid var(--color-cocoa-100)',
            background: DARK_WIRES.has(kind) ? 'var(--color-cocoa-900)' : 'var(--color-cocoa-0)',
        }}
    >
        {wireBody(kind)}
    </div>
);
